<script setup lang="ts">
import { inkCss } from '@vp/render';
import { canvasDark } from '../theme';
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { CoreTypes, DEFAULT_FONT_FAMILY, SPAN_SIZE_MAX, SPAN_SIZE_MIN, cleanLook, hasFormatting, normalizeSpans, readSpans, spanFamilies, spansText, type SpanLook, type TextSpan } from '@vp/shared-types';
import { DEFAULT_TEXT_STYLE } from '@vp/core';
import { arrowLabelLayout, formTextInset, formTextStyle, frameTitleHeight, textBoxHeight, textPadding } from '@vp/render';
import { live, ui } from '../editor/bridge';
import { FONTS, loadFamiliesOf } from '../fonts';
import { t, typeLabel } from '../i18n';
import { moduleTextInfo, moduleTypeLabel } from '../modules';
import Icon from './Icon.vue';

/** Colors as the canvas shows them (inverted with the hue kept on the dark canvas). */
const ink = (c: string): string => (canvasDark.value ? inkCss(c) : c);

/**
 * In-place text editing over the object (§5.5): native IME, selection and caret; the canvas hides
 * the object's text while editing. Text of shapes, stickers and text objects is formatted by
 * fragments, like in Word (bold, italic, underline, strike-through, color, highlight, font, size):
 * a contenteditable with a floating formatting bar. Frame titles and line labels stay plain.
 */
const area = ref<HTMLTextAreaElement | null>(null);
const ed = ref<HTMLDivElement | null>(null);
const value = ref('');
const tick = ref(0);
let unsubCam: (() => void) | null = null;
let committed = false;

const TEXT_COLORS = ['#1f2937', '#dc2626', '#ea580c', '#16a34a', '#2563eb', '#7c3aed', '#6b7280', '#ffffff'];
const HIGHLIGHTS = ['#fef08a', '#bbf7d0', '#a5f3fc', '#fbcfe8', '#fed7aa', '#e9d5ff'];
const SIZE_STEP = 1.2;

const obj = computed(() => {
  void tick.value;
  void ui.docRev;
  return ui.editingText && live.editor ? live.editor.doc.get(ui.editingText) ?? null : null;
});

const key = computed(() => {
  const o = obj.value;
  if (!o) return 'text';
  return o.type === CoreTypes.frame ? 'title' : o.type === CoreTypes.connector || o.type === CoreTypes.line ? 'label' : 'text';
});
/** Formatted by fragments: the text of shapes, stickers and text objects. */
const rich = computed(() => key.value === 'text');

const style = computed(() => {
  void tick.value;
  const o = obj.value;
  const editor = live.editor;
  if (!o || !editor) return {};
  const box = editor.screenBox(o.id);
  if (!box) return {};
  const ts = o.style.text ?? DEFAULT_TEXT_STYLE;
  const z = box.zoom;
  const isLine = o.type === CoreTypes.connector || o.type === CoreTypes.line;
  if (o.type === CoreTypes.frame) {
    // frame title lives in the strip above the frame
    const th = frameTitleHeight(o) * z;
    const lineH = ts.fontSize * (ts.lineHeight ?? 1.25) * z;
    return {
      left: box.x + 'px',
      top: box.y - th + 'px',
      width: Math.max(box.w, 160) + 'px',
      height: th + 'px',
      transform: 'none',
      fontSize: ts.fontSize * z + 'px',
      lineHeight: String(ts.lineHeight ?? 1.25),
      fontFamily: ts.fontFamily ?? DEFAULT_FONT_FAMILY,
      fontWeight: ts.bold ? '700' : '400',
      fontStyle: ts.italic ? 'italic' : 'normal',
      color: ink(ts.color),
      textAlign: 'left' as const,
      padding: Math.max(0, (th - lineH) / 2) + 'px ' + textPadding(ts) * z + 'px',
      background: ink('#ffffff'),
      whiteSpace: 'nowrap',
    };
  }
  if (isLine) {
    // where the label is drawn (above the line or in its break), see-through: the arrow stays visible
    const lay = arrowLabelLayout(o, value.value || ' ');
    if (lay) {
      const lw = Math.max(lay.w * z, 48);
      const lh = lay.style.fontSize * (ts.lineHeight ?? 1.25) * z;
      return {
        left: box.x + lay.x * z + (lay.w * z - lw) / 2 + 'px',
        top: box.y + lay.y * z + (lay.h * z - lh) / 2 + 'px',
        width: lw + 'px',
        height: lh + 'px',
        transform: 'none',
        fontSize: lay.style.fontSize * z + 'px',
        lineHeight: String(ts.lineHeight ?? 1.25),
        fontFamily: ts.fontFamily ?? DEFAULT_FONT_FAMILY,
        fontWeight: ts.bold ? '700' : '400',
        fontStyle: ts.italic ? 'italic' : 'normal',
        color: ink(ts.color),
        textAlign: 'center' as const,
        padding: '0px',
        background: 'transparent',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
      };
    }
  }
  const minW = isLine ? 160 : 0;
  const w = Math.max(box.w, minW);
  const h = isLine ? Math.max(ts.fontSize * 2 * z, 32) : box.h;
  const vPad = ts.valign === 'top' ? textPadding(ts) * z : Math.max(0, (box.h - ts.fontSize * (ts.lineHeight ?? 1.25) * z * Math.max(1, value.value.split('\n').length)) / 2);
  return {
    left: box.x + (box.w - w) / 2 + 'px',
    top: (isLine ? box.y + box.h / 2 - h / 2 : box.y) + 'px',
    width: w + 'px',
    height: (o.type === CoreTypes.text ? Math.max(h, ts.fontSize * 1.6 * z) : h) + 'px',
    transform: `rotate(${box.rotation}rad)`,
    fontSize: ts.fontSize * z + 'px',
    lineHeight: String(ts.lineHeight ?? 1.25),
    fontFamily: ts.fontFamily ?? DEFAULT_FONT_FAMILY,
    fontWeight: ts.bold ? '700' : '400',
    fontStyle: ts.italic ? 'italic' : 'normal',
    color: ink(ts.color),
    textAlign: ts.align as 'left' | 'center' | 'right',
    padding: `${o.type === CoreTypes.text || ts.valign === 'top' ? textPadding(ts) * z : vPad}px ${textPadding(ts) * z}px`,
    background: isLine ? ink('#ffffff') : 'transparent',
  };
});

/** Box of a formatted editor (the object), and the text inside it: aligned like on the canvas. */
const boxStyle = computed(() => {
  void tick.value;
  const o = obj.value;
  const box = o && live.editor?.screenBox(o.id);
  if (!o || !box) return {};
  const mt = moduleTextInfo(o.type);
  const ts = formTextStyle(o, o.style.text ?? mt?.style ?? DEFAULT_TEXT_STYLE);
  const pad = textPadding(ts) * box.zoom;
  const valign = o.type === CoreTypes.text ? 'top' : ts.valign;
  // a module object's text area: the object minus the type's inset; turns about the object's center
  const z = box.zoom;
  const ins = mt?.inset ?? formTextInset(o) ?? { top: 0, right: 0, bottom: 0, left: 0 };
  const w = Math.max(1, box.w - (ins.left + ins.right) * z);
  const h = Math.max(1, box.h - (ins.top + ins.bottom) * z);
  return {
    left: box.x + ins.left * z + 'px',
    top: box.y + ins.top * z + 'px',
    width: w + 'px',
    minHeight: (o.type === CoreTypes.text ? Math.max(box.h, ts.fontSize * 1.6 * box.zoom) : h) + 'px',
    height: o.type === CoreTypes.text || mt?.fit ? 'auto' : h + 'px',
    transformOrigin: `${box.w / 2 - ins.left * z}px ${box.h / 2 - ins.top * z}px`,
    transform: `rotate(${box.rotation}rad)`,
    justifyContent: valign === 'top' ? 'flex-start' : valign === 'bottom' ? 'flex-end' : 'center',
    padding: `${pad}px ${pad}px`,
  };
});
const innerStyle = computed(() => {
  void tick.value;
  const o = obj.value;
  const box = o && live.editor?.screenBox(o.id);
  if (!o || !box) return {};
  const ts = formTextStyle(o, o.style.text ?? moduleTextInfo(o.type)?.style ?? DEFAULT_TEXT_STYLE);
  return {
    fontSize: ts.fontSize * box.zoom + 'px',
    lineHeight: String(ts.lineHeight ?? 1.25),
    fontFamily: ts.fontFamily ?? DEFAULT_FONT_FAMILY,
    fontWeight: ts.bold ? '700' : '400',
    fontStyle: ts.italic ? 'italic' : 'normal',
    color: ink(ts.color),
    textAlign: ts.align as 'left' | 'center' | 'right',
  };
});

/** The object's name for the top bar (like the properties bar shows it). */
const objName = computed(() => (obj.value ? obj.value.name || typeLabel(obj.value.type, moduleTypeLabel) : ''));
const objIcon = computed(() => (obj.value?.type.startsWith('core:') ? obj.value.type.slice(5) : 'module'));

// ---------------------------------------------------------------- formatted model
let model: TextSpan[] = [];
/** Look for the next typed characters (a format chosen with no selection, like in Word). */
let pending: SpanLook | null = null;
const menu = ref<'' | 'color' | 'bg' | 'font'>('');
/** What the selection looks like (for the bar's active states). */
const current = ref<{ b: boolean; i: boolean; u: boolean; s: boolean; c?: string; bg?: string; f?: string }>({ b: false, i: false, u: false, s: false });

function spanCss(el: HTMLElement, l: SpanLook): void {
  el.style.fontWeight = l.b ? '700' : '';
  el.style.fontStyle = l.i ? 'italic' : '';
  el.style.textDecoration = [l.u ? 'underline' : '', l.s ? 'line-through' : ''].filter(Boolean).join(' ');
  el.style.color = l.c ? ink(l.c) : '';
  el.style.background = l.bg ? ink(l.bg) : '';
  el.style.fontFamily = l.f ?? '';
  el.style.fontSize = l.k ? l.k + 'em' : '';
}

function look(s: TextSpan): SpanLook {
  return cleanLook(s);
}

function renderModel(): void {
  const el = ed.value;
  if (!el) return;
  el.replaceChildren();
  for (const sp of model) {
    const s = document.createElement('span');
    s.dataset['look'] = JSON.stringify(look(sp));
    spanCss(s, look(sp));
    s.textContent = sp.t;
    el.append(s);
  }
  // a caret place after a final line break / in an empty editor
  const br = document.createElement('br');
  br.dataset['tail'] = '1';
  el.append(br);
}

function lastLeaf(n: Node): Node {
  let x: Node = n;
  while (x.lastChild) x = x.lastChild;
  return x;
}

/** The editor's DOM → fragments (what the browser typed, deleted or composed). */
function readDom(): TextSpan[] {
  const el = ed.value;
  if (!el) return model;
  const out: TextSpan[] = [];
  const tail = lastLeaf(el);
  const walk = (n: Node, l: SpanLook) => {
    for (const c of Array.from(n.childNodes)) {
      if (c.nodeType === Node.TEXT_NODE) {
        // text the browser typed outside any fragment (after a line break at the end): it continues
        // the look of the text before it, like in Word
        const own = n === el && out.length ? cleanLook(out[out.length - 1]) : l;
        out.push({ t: (c as Text).data.replace(/\u200b/g, ''), ...own });
      }
      else if (c instanceof HTMLElement) {
        if (c.tagName === 'BR') {
          if (!c.dataset['tail'] && c !== tail) out.push({ t: '\n', ...l });
          continue;
        }
        const block = c.tagName === 'DIV' || c.tagName === 'P';
        const so = spansText(out);
        if (block && so && !so.endsWith('\n')) out.push({ t: '\n', ...l });
        let cl = l;
        if (c.dataset['look']) {
          try {
            cl = cleanLook(JSON.parse(c.dataset['look']));
          } catch {
            cl = l;
          }
        }
        walk(c, cl);
      }
    }
  };
  walk(el, {});
  return normalizeSpans(out);
}

/** Character index of a DOM point inside the editor. */
function pointToIndex(node: Node, offset: number): number {
  const el = ed.value!;
  const r = document.createRange();
  r.setStart(el, 0);
  r.setEnd(node, offset);
  let n = 0;
  const walk = (x: Node) => {
    for (const c of Array.from(x.childNodes)) {
      if (c.nodeType === Node.TEXT_NODE) n += (c as Text).data.replace(/\u200b/g, '').length;
      else if (c instanceof HTMLElement) {
        if (c.tagName === 'BR') {
          if (!c.dataset['tail']) n++;
        } else walk(c);
      }
    }
  };
  walk(r.cloneContents());
  return n;
}

function indexToPoint(i: number): { node: Node; offset: number } {
  const el = ed.value!;
  const w = document.createTreeWalker(el, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
  let left = i;
  for (let n = w.nextNode(); n; n = w.nextNode()) {
    if (n.nodeType === Node.TEXT_NODE) {
      const len = (n as Text).data.length;
      if (left <= len) return { node: n, offset: left };
      left -= len;
    } else if (n instanceof HTMLElement && n.tagName === 'BR' && !n.dataset['tail']) {
      if (left === 0) return { node: n.parentNode!, offset: Array.prototype.indexOf.call(n.parentNode!.childNodes, n) };
      left--;
    }
  }
  const tail = el.lastChild;
  const at = tail instanceof HTMLElement && tail.dataset['tail'] ? el.childNodes.length - 1 : el.childNodes.length;
  return { node: el, offset: at };
}

function getSel(): { a: number; b: number } {
  const s = window.getSelection();
  const el = ed.value;
  if (!s || !s.rangeCount || !el) return { a: 0, b: 0 };
  const r = s.getRangeAt(0);
  if (!el.contains(r.startContainer)) return { a: spansText(model).length, b: spansText(model).length };
  const a = pointToIndex(r.startContainer, r.startOffset);
  const b = pointToIndex(r.endContainer, r.endOffset);
  return a <= b ? { a, b } : { a: b, b: a };
}

function setSel(a: number, b: number): void {
  const s = window.getSelection();
  if (!s || !ed.value) return;
  const p = indexToPoint(a);
  const q = indexToPoint(b);
  const r = document.createRange();
  r.setStart(p.node, p.offset);
  r.setEnd(q.node, q.offset);
  s.removeAllRanges();
  s.addRange(r);
}

/** Split the model so that a fragment boundary falls at i. */
function splitAt(i: number): void {
  let pos = 0;
  for (let k = 0; k < model.length; k++) {
    const sp = model[k];
    const end = pos + sp.t.length;
    if (i > pos && i < end) {
      model.splice(k, 1, { ...sp, t: sp.t.slice(0, i - pos) }, { ...sp, t: sp.t.slice(i - pos) });
      return;
    }
    pos = end;
  }
}

function mapRange(a: number, b: number, f: (l: SpanLook) => SpanLook): void {
  splitAt(a);
  splitAt(b);
  let pos = 0;
  model = model.map((sp) => {
    const start = pos;
    pos += sp.t.length;
    return start >= a && pos <= b ? { t: sp.t, ...cleanLook(f(look(sp))) } : sp;
  });
  model = normalizeSpans(model);
}

function looksIn(a: number, b: number): SpanLook[] {
  const out: SpanLook[] = [];
  let pos = 0;
  for (const sp of model) {
    const start = pos;
    pos += sp.t.length;
    if (pos > a && start < b) out.push(look(sp));
  }
  return out;
}

/** Look of the text at a caret: of the character before it (the first one at the start). */
function lookAt(i: number): SpanLook {
  let pos = 0;
  for (const sp of model) {
    pos += sp.t.length;
    if (i <= pos && sp.t.length) return look(sp);
  }
  return model.length ? look(model[model.length - 1]) : {};
}

function replaceRange(a: number, b: number, ins: TextSpan[]): void {
  splitAt(a);
  splitAt(b);
  const out: TextSpan[] = [];
  let pos = 0;
  let placed = false;
  for (const sp of model) {
    const start = pos;
    pos += sp.t.length;
    if (!placed && start >= a) {
      out.push(...ins);
      placed = true;
    }
    if (start >= a && pos <= b && sp.t.length) continue;
    out.push(sp);
  }
  if (!placed) out.push(...ins);
  model = normalizeSpans(out);
}

function refresh(a: number, b: number): void {
  renderModel();
  ed.value?.focus({ preventScroll: true });
  setSel(a, b);
  updateCurrent();
  void nextTick(fitFrame);
}

function updateCurrent(): void {
  if (!ed.value) return;
  const { a, b } = getSel();
  const ls = pending ? [pending] : a === b ? [lookAt(a)] : looksIn(a, b);
  const all = (k: 'b' | 'i' | 'u' | 's') => ls.length > 0 && ls.every((l) => !!l[k]);
  const one = (k: 'c' | 'bg' | 'f') => (ls.length && ls.every((l) => l[k] === ls[0][k]) ? ls[0][k] : undefined);
  current.value = { b: all('b'), i: all('i'), u: all('u'), s: all('s'), c: one('c'), bg: one('bg'), f: one('f') };
}

/** Apply a change of look to the selection, or to what is typed next when nothing is selected. */
function format(f: (l: SpanLook) => SpanLook): void {
  if (!ed.value) return;
  model = readDom();
  const { a, b } = getSel();
  if (a === b) {
    pending = cleanLook(f(pending ?? lookAt(a)));
    pendingAt = a;
    updateCurrent();
    ed.value.focus({ preventScroll: true });
    return;
  }
  pending = null;
  mapRange(a, b, f);
  refresh(a, b);
}

function toggle(k: 'b' | 'i' | 'u' | 's'): void {
  model = readDom();
  const { a, b } = getSel();
  const ls = a === b ? [pending ?? lookAt(a)] : looksIn(a, b);
  const on = ls.length > 0 && ls.every((l) => !!l[k]);
  format((l) => ({ ...l, [k]: on ? undefined : true }));
}

function setField(k: 'c' | 'bg' | 'f', v: string | undefined): void {
  menu.value = '';
  format((l) => ({ ...l, [k]: v }));
  if (k === 'f' && v) loadFamiliesOf([v]);
}

function resize(mult: number): void {
  format((l) => ({ ...l, k: Math.min(SPAN_SIZE_MAX, Math.max(SPAN_SIZE_MIN, (l.k ?? 1) * mult)) }));
}

function clearFormatting(): void {
  format(() => ({}));
}

function onBeforeInput(e: InputEvent): void {
  if (e.inputType.startsWith('format')) {
    // the browser's own formatting would put <b>/<i> tags the model does not know
    e.preventDefault();
    return;
  }
  if (!pending || e.inputType !== 'insertText' || !e.data) return;
  e.preventDefault();
  model = readDom();
  const { a, b } = getSel();
  replaceRange(a, b, [{ t: e.data, ...pending }]);
  pending = null;
  refresh(a + e.data.length, a + e.data.length);
}

function insertPlain(text: string): void {
  model = readDom();
  const { a, b } = getSel();
  const l = pending ?? lookAt(a);
  pending = null;
  replaceRange(a, b, [{ t: text, ...l }]);
  refresh(a + text.length, a + text.length);
}

function onPaste(e: ClipboardEvent): void {
  e.preventDefault();
  const text = (e.clipboardData?.getData('text/plain') ?? '').replace(/\r\n?/g, '\n');
  if (text) insertPlain(text);
}

function onInput(): void {
  model = readDom();
  tick.value++;
  void nextTick(fitFrame);
}

/** Wheel over the edited text pans and zooms the canvas like anywhere else (editing goes on). */
function forwardWheel(e: WheelEvent): void {
  e.preventDefault();
  e.stopPropagation();
  const target = live.editor?.renderer.container;
  if (!target) return;
  const { deltaX, deltaY, deltaZ, deltaMode, clientX, clientY, screenX, screenY, ctrlKey, metaKey, shiftKey, altKey, buttons } = e;
  target.dispatchEvent(new WheelEvent('wheel', { deltaX, deltaY, deltaZ, deltaMode, clientX, clientY, screenX, screenY, ctrlKey, metaKey, shiftKey, altKey, buttons, bubbles: true, cancelable: true }));
}

function onPlainInput(): void {
  tick.value++;
  if (ui.editingText) live.editor?.typeLabel(ui.editingText, value.value);
  void nextTick(fitFrame);
}

/** Grow the object to fit the text (same layout as the canvas; zoom does not change world height). */
function fitFrame(): void {
  const id = ui.editingText;
  const editor = live.editor;
  if (!id || !editor) return;
  const o = editor.doc.get(id);
  if (!o || o.type !== CoreTypes.text || !o.style.text) return;
  const st = o.style.text;
  const w = o.w;
  const h = rich.value
    ? textBoxHeight(spansText(model), hasFormatting(model) ? model : null, st, w)
    : textBoxHeight(value.value, null, st, w);
  editor.growTextFrame(id, h);
}

function onSelectionChange(): void {
  const s = window.getSelection();
  if (!ed.value || !s || !s.rangeCount || !ed.value.contains(s.anchorNode)) return;
  // moving the caret drops a format chosen for "the next characters"
  if (pending) {
    const { a, b } = getSel();
    if (a !== b || a !== pendingAt) pending = null;
  }
  updateCurrent();
}
let pendingAt = -1;

watch(
  () => ui.editingText,
  async (id) => {
    unsubCam?.();
    unsubCam = null;
    menu.value = '';
    pending = null;
    if (!id || !live.editor) return;
    committed = false;
    const o = live.editor.doc.get(id);
    value.value = (o?.props[key.value] as string | undefined) ?? '';
    unsubCam = live.renderer?.camera.onChange(() => tick.value++) ?? null;
    if (rich.value) {
      model = readSpans(value.value, o?.props['spans']) ?? (value.value ? [{ t: value.value }] : []);
      loadFamiliesOf(spanFamilies(model));
    }
    await nextTick();
    if (rich.value && ed.value) {
      renderModel();
      ed.value.focus({ preventScroll: true });
      const r = document.createRange();
      r.selectNodeContents(ed.value);
      if (ed.value.lastChild) r.setEndBefore(ed.value.lastChild);
      window.getSelection()?.removeAllRanges();
      window.getSelection()?.addRange(r);
      updateCurrent();
      void nextTick(fitFrame);
    } else {
      area.value?.focus({ preventScroll: true });
      area.value?.select();
    }
  },
);

document.addEventListener('selectionchange', onSelectionChange);
onBeforeUnmount(() => {
  unsubCam?.();
  document.removeEventListener('selectionchange', onSelectionChange);
});

/** A click on the object around the text: caret to the end, editing goes on. */
function focusEnd(): void {
  menu.value = '';
  ed.value?.focus({ preventScroll: true });
  const n = spansText(readDom()).length;
  setSel(n, n);
}

function commit(): void {
  if (committed || !ui.editingText) return;
  committed = true;
  const id = ui.editingText;
  if (rich.value) {
    model = readDom();
    live.editor?.commitText(id, spansText(model), model);
  } else live.editor?.commitText(id, value.value);
  menu.value = '';
  live.editor?.renderer.container.focus();
}

function onBlur(e: FocusEvent): void {
  // focus moving into the formatting bar is not leaving the editor
  if (e.relatedTarget instanceof Node && bar.value?.contains(e.relatedTarget)) return;
  commit();
}
const bar = ref<HTMLDivElement | null>(null);

function onKey(e: KeyboardEvent): void {
  e.stopPropagation();
  if (e.key === 'Escape') {
    e.preventDefault();
    if (menu.value) {
      menu.value = '';
      return;
    }
    // Escape keeps typed text (like most whiteboards) but leaves edit mode
    commit();
  } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
    e.preventDefault();
    commit();
  } else if (rich.value && e.key === 'Enter') {
    e.preventDefault();
    insertPlain('\n');
  } else if (rich.value && (e.ctrlKey || e.metaKey) && !e.altKey) {
    // layout-independent: the physical key
    const k = e.code === 'KeyB' ? 'b' : e.code === 'KeyI' ? 'i' : e.code === 'KeyU' ? 'u' : '';
    if (k) {
      e.preventDefault();
      toggle(k);
    }
  }
}

const fontChoices = computed(() => FONTS.filter((f) => f.id !== 'system'));
</script>

<template>
  <textarea
    v-if="obj && !rich"
    ref="area"
    v-model="value"
    class="text-editor"
    :style="style"
    spellcheck="true"
    @blur="commit"
    @keydown="onKey"
    @pointerdown.stop
    @wheel="forwardWheel"
    @input="onPlainInput"
  ></textarea>
  <template v-else-if="obj">
    <div class="rich-box" :style="boxStyle" @pointerdown.stop @wheel="forwardWheel" @mousedown.self.prevent="focusEnd">
      <div
        ref="ed"
        class="rich-text"
        :style="innerStyle"
        contenteditable="true"
        spellcheck="true"
        role="textbox"
        aria-multiline="true"
        @blur="onBlur"
        @keydown="onKey"
        @beforeinput="onBeforeInput"
        @paste="onPaste"
        @input="onInput"
      ></div>
    </div>
    <!-- same pill as the properties bar, which is hidden while text is edited -->
    <div ref="bar" v-show="!ui.zen" class="rich-bar-wrap" @pointerdown.stop @mousedown.prevent @wheel.stop>
    <div class="float pillbar rich-bar" role="toolbar" :aria-label="t('rich.toolbar')">
      <span class="name static"><Icon :name="objIcon" :size="15" /><span class="name-text">{{ objName }}</span></span>
      <span class="sep"></span>
      <button class="chip" :class="{ on: current.b }" :title="t('rich.bold')" :aria-label="t('rich.bold')" :aria-pressed="current.b" @click="toggle('b')"><Icon name="bold" :size="15" /></button>
      <button class="chip" :class="{ on: current.i }" :title="t('rich.italic')" :aria-label="t('rich.italic')" :aria-pressed="current.i" @click="toggle('i')"><Icon name="italic" :size="15" /></button>
      <button class="chip" :class="{ on: current.u }" :title="t('rich.underline')" :aria-label="t('rich.underline')" :aria-pressed="current.u" @click="toggle('u')"><Icon name="underline" :size="15" /></button>
      <button class="chip" :class="{ on: current.s }" :title="t('rich.strike')" :aria-label="t('rich.strike')" :aria-pressed="current.s" @click="toggle('s')"><Icon name="strike" :size="15" /></button>
      <span class="sep"></span>
      <span class="pop">
        <button class="chip" data-menu="color" :class="{ on: menu === 'color' }" :title="t('rich.color')" :aria-label="t('rich.color')" @click="menu = menu === 'color' ? '' : 'color'">
          <Icon name="textColor" :size="15" /><span class="under" :style="{ background: current.c ? ink(current.c) : 'var(--text)' }"></span>
        </button>
        <div v-if="menu === 'color'" class="float card rich-menu swatches">
          <button class="swatch-btn none" :title="t('rich.defaultColor')" :aria-label="t('rich.defaultColor')" @click="setField('c', undefined)"></button>
          <button v-for="c in TEXT_COLORS" :key="c" class="swatch-btn" :class="{ active: current.c === c }" :style="{ background: ink(c) }" :aria-label="c" @click="setField('c', c)"></button>
        </div>
      </span>
      <span class="pop">
        <button class="chip" data-menu="bg" :class="{ on: menu === 'bg' }" :title="t('rich.highlight')" :aria-label="t('rich.highlight')" @click="menu = menu === 'bg' ? '' : 'bg'">
          <Icon name="highlight" :size="15" /><span class="under" :style="{ background: current.bg ? ink(current.bg) : 'transparent' }"></span>
        </button>
        <div v-if="menu === 'bg'" class="float card rich-menu swatches">
          <button class="swatch-btn none" :title="t('rich.noHighlight')" :aria-label="t('rich.noHighlight')" @click="setField('bg', undefined)"></button>
          <button v-for="c in HIGHLIGHTS" :key="c" class="swatch-btn" :class="{ active: current.bg === c }" :style="{ background: ink(c) }" :aria-label="c" @click="setField('bg', c)"></button>
        </div>
      </span>
      <span class="pop">
        <button class="chip" data-menu="font" :class="{ on: menu === 'font' }" :title="t('rich.font')" :aria-label="t('rich.font')" @click="menu = menu === 'font' ? '' : 'font'"><Icon name="font" :size="15" /></button>
        <div v-if="menu === 'font'" class="float card rich-menu fonts" role="menu">
          <button role="menuitem" :class="{ on: !current.f }" @click="setField('f', undefined)">{{ t('rich.defaultFont') }}</button>
          <button v-for="f in fontChoices" :key="f.id" role="menuitem" :class="{ on: current.f === f.family }" :style="{ fontFamily: f.family }" @click="setField('f', f.family)">{{ t(f.label) }}</button>
        </div>
      </span>
      <button class="chip" :title="t('rich.smaller')" :aria-label="t('rich.smaller')" @click="resize(1 / SIZE_STEP)"><Icon name="smaller" :size="15" /></button>
      <button class="chip" :title="t('rich.larger')" :aria-label="t('rich.larger')" @click="resize(SIZE_STEP)"><Icon name="larger" :size="15" /></button>
      <span class="sep"></span>
      <button class="icon-btn" :title="t('rich.clear')" :aria-label="t('rich.clear')" @click="clearFormatting"><Icon name="clearFormat" :size="16" /></button>
      <span class="sep"></span>
      <button class="chip done" :title="t('rich.doneHint')" @click="commit"><Icon name="check" :size="14" />{{ t('rich.done') }}</button>
    </div>
    </div>
  </template>
</template>

<style scoped>
.text-editor {
  position: absolute;
  z-index: 30;
  border: none;
  outline: none;
  resize: none;
  overflow: hidden;
  transform-origin: center center;
  margin: 0;
  white-space: pre-wrap;
  overflow-wrap: break-word;
}
.rich-box {
  position: absolute;
  z-index: 30;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  outline: none;
  transform-origin: center center;
  overflow: visible;
  cursor: text;
}
.rich-text,
.rich-text:focus,
.rich-text:focus-visible {
  outline: none;
  box-shadow: none;
}
.rich-text {
  flex: none;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  min-height: 1em;
  caret-color: currentColor;
}
/* the place and look of the properties bar (PropertiesBar.vue), which is hidden while text is edited */
.rich-bar-wrap {
  position: absolute;
  top: 12px;
  left: 0;
  right: 0;
  margin: 0 auto;
  width: max-content;
  max-width: calc(100% - 24px);
  z-index: 31;
}
.rich-bar {
  position: static;
  gap: 2px;
  padding: 4px 6px;
}
.rich-bar .done {
  color: var(--accent-text);
  font-weight: 600;
}
.rich-bar .chip {
  position: relative;
}
.pop {
  position: relative;
  display: inline-flex;
}
.chip .under {
  position: absolute;
  left: 7px;
  right: 7px;
  bottom: 4px;
  height: 3px;
  border-radius: 2px;
}
.rich-menu {
  /* solid: the text under the menu must not show through */
  background: var(--panel);
  backdrop-filter: none;
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  padding: 8px;
}
.rich-menu.swatches {
  display: flex;
  gap: 6px;
}
.rich-menu.fonts {
  display: flex;
  flex-direction: column;
  padding: 4px;
  min-width: 170px;
}
.rich-menu.fonts button {
  height: 30px;
  padding: 0 10px;
  border: none;
  background: transparent;
  border-radius: 8px;
  text-align: left;
  font-size: 14px;
  color: var(--text);
  cursor: pointer;
  white-space: nowrap;
}
.rich-menu.fonts button:hover {
  background: var(--panel-2);
}
.rich-menu.fonts button.on {
  background: var(--accent-soft);
  color: var(--accent-text);
}
</style>
